import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trunkUsernameNumber, type Destination } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker } from '../../components/telephony/DestinationPicker.js';

/** 0872650584 from +27872650584: how the account's people write their own numbers. */
const national = (e164: string) => (e164.startsWith('+27') ? `0${e164.slice(3)}` : e164);

/**
 * The trunk's own number as an inbound number of the account (owner, 27 September 2026). Offered
 * only when the trunk registers AS a phone number -- Afrihost's username is the VoIP number --
 * and only that number: any other is added by the provider (API owntrunks.ts .../number).
 * Used in the trunk wizard's last step and on each trunk in the list.
 */
export function TrunkNumberOffer({ tenantId, trunk }: {
  tenantId: string;
  trunk: { id: string; auth_mode: string; auth_username: string | null; inbound_number?: { e164: string; routed: boolean } | null };
}) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const e164 = trunk.auth_mode === 'register' ? trunkUsernameNumber(trunk.auth_username) : null;
  const [on, setOn] = useState(true);
  const [dest, setDest] = useState<Destination | null>(null);
  const add = useMutation({
    mutationFn: () => api.post<{ e164: string; route_id: string | null }>(
      `/v1/tenants/${tenantId}/own-trunks/${trunk.id}/number`, dest ? { destination: dest } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['own-trunks', tenantId] });
      void qc.invalidateQueries({ queryKey: ['inbound-routes', tenantId] });
    },
  });
  if (!e164 || !can('connectivity:write')) return null;

  if (trunk.inbound_number) {
    return (
      <p className="arc-hint" style={{ margin: 0 }}>
        <span className="arc-mono">{national(trunk.inbound_number.e164)}</span> is an inbound number of this account.
        {!trunk.inbound_number.routed && ' Choose where its calls go under Inbound routes.'}
      </p>
    );
  }
  return (
    <div className="arc-stack" style={{ gap: 8 }}>
      <label className="arc-row" style={{ gap: 10, fontSize: 12.5 }}>
        <Switch id={`tn-${trunk.id}`} checked={on} onChange={setOn} />
        Add <span className="arc-mono">{national(e164)}</span> as an inbound number
      </label>
      {on && (
        <>
          {can('inboundroute:write') && (
            <div className="arc-field" style={{ margin: 0, maxWidth: 360 }}>
              <label className="arc-label" htmlFor={`tn-dest-${trunk.id}`}>Send its calls to</label>
              <DestinationPicker tenantId={tenantId} value={dest} onChange={setDest} allowNone />
            </div>
          )}
          {add.error && <ErrorBanner error={add.error} />}
          <div>
            <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" disabled={add.isPending} onClick={() => add.mutate()}>
              {add.isPending ? 'Adding…' : 'Add number'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
